import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useGrowthStore } from '../../store/useGrowthStore';
import { type GrowthPriority, type Recurrence } from '../../store/useTodoStore';
import { cn } from '../../lib/utils';
import { AppSelectMenu } from '../../components/AppSelectMenu';
import {
  APP_MODAL_CARD_CLASS,
  APP_MODAL_CLOSE_CLASS,
  APP_GREEN_GLASS_BUTTON_STYLE,
  APP_GREEN_GLASS_TEXT,
  APP_MODAL_INPUT_CLASS,
  APP_MODAL_OVERLAY_CLASS,
  APP_MODAL_TITLE_CLASS,
  APP_SELECTED_GLOW_BG,
  APP_SELECTED_GLOW_BORDER,
  APP_SELECTED_GLOW_SHADOW,
} from '../../lib/modalTheme';
import { triggerLightHaptic } from '../../lib/haptics';
import {
  getGrowthPrioritySelectedStyle,
  GROWTH_PRIORITY_TEXT_CLASS,
} from './growthTodoPriorityStyles';

interface DefaultValues {
  title?: string;
  bottleId?: string;
  recurrence?: Recurrence;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (input: {
    title: string;
    priority: GrowthPriority;
    bottleId?: string;
    dueAt?: number;
    recurrence?: Recurrence;
    recurrenceDays?: number[];
  }) => void;
  defaultValues?: DefaultValues;
}

export const AddGrowthTodoModal = ({ isOpen, onClose, onAdd, defaultValues }: Props) => {
  const { t } = useTranslation();
  const weekdayLabels = [
    t('weekday_short_sun'),
    t('weekday_short_mon'),
    t('weekday_short_tue'),
    t('weekday_short_wed'),
    t('weekday_short_thu'),
    t('weekday_short_fri'),
    t('weekday_short_sat'),
  ];
  const bottles = useGrowthStore((s) => s.bottles.filter((b) => b.status === 'active'));
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<GrowthPriority>('medium');
  const [bottleId, setBottleId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [recurrence, setRecurrence] = useState<Recurrence>('once');
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
  const [titleError, setTitleError] = useState(false);
  const selectedGlowStyle = {
    background: APP_SELECTED_GLOW_BG,
    border: APP_SELECTED_GLOW_BORDER,
    boxShadow: APP_SELECTED_GLOW_SHADOW,
  };

  // Apply defaults when modal opens
  useEffect(() => {
    if (isOpen && defaultValues) {
      if (defaultValues.title) setTitle(defaultValues.title);
      if (defaultValues.bottleId) setBottleId(defaultValues.bottleId);
      if (defaultValues.recurrence) setRecurrence(defaultValues.recurrence);
    }
  }, [isOpen, defaultValues]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    onAdd({
      title: title.trim(),
      priority,
      bottleId: bottleId || undefined,
      dueAt: dueAt ? new Date(dueAt).getTime() : undefined,
      recurrence,
      recurrenceDays: recurrence === 'weekly' ? recurrenceDays : undefined,
    });
    // Reset form
    setTitle('');
    setPriority('medium');
    setBottleId('');
    setDueAt('');
    setRecurrence('once');
    setRecurrenceDays([]);
    setTitleError(false);
    onClose();
  };

  const toggleDay = (day: number) => {
    setRecurrenceDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const priorities: GrowthPriority[] = ['high', 'medium', 'low'];
  const recurrences: Recurrence[] = ['once', 'daily', 'weekly'];

  return (
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', APP_MODAL_OVERLAY_CLASS)}>
      <div className={cn(APP_MODAL_CARD_CLASS, 'animate-in zoom-in-95 fade-in w-[min(92vw,420px)] max-h-[86vh] overflow-hidden rounded-2xl')}>
        <div className="flex max-h-[86vh] flex-col">
          <div className="flex items-center justify-between px-5 pb-3 pt-5">
            <h3 className={APP_MODAL_TITLE_CLASS}>{t('growth_todo_add')}</h3>
            <button onClick={onClose} className={cn(APP_MODAL_CLOSE_CLASS, 'p-1')}><X size={24} strokeWidth={1.5} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 pb-3 [overscroll-behavior:contain] [-webkit-overflow-scrolling:touch]">
            {/* Title */}
            <input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setTitleError(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
              enterKeyHint="done"
              placeholder={t('growth_todo_title_placeholder')}
              className={cn(
                APP_MODAL_INPUT_CLASS,
                'mb-1 w-full p-3',
                titleError ? 'border-red-400' : 'border-gray-200'
              )}
            />
            {titleError && (
              <p className="app-caption mb-3 text-red-500">{t('growth_todo_title_required')}</p>
            )}

            {/* Due date */}
            <label className="app-body mb-2 mt-3 block font-medium text-slate-600">
              {t('growth_todo_due_datetime')}
            </label>
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className={cn(APP_MODAL_INPUT_CLASS, 'mb-4 w-full p-3')}
            />

            {/* Recurrence */}
            <label className="app-body mb-2 block font-medium text-slate-600">
              {t('growth_todo_recurrence')}
            </label>
            <div className="flex gap-2 mb-4">
              {recurrences.map((r) => (
                <button
                  key={r}
                  onClick={() => {
                    triggerLightHaptic();
                    setRecurrence(r);
                  }}
                  className={cn(
                    'app-body flex-1 rounded-xl border py-2 font-medium transition-all',
                    recurrence === r
                      ? 'text-[#1D4ED8]'
                      : 'border-white/80 bg-white/70 text-[#2F3E33]'
                  )}
                  style={recurrence === r ? selectedGlowStyle : undefined}
                >
                  {t(`growth_todo_recurrence_${r}`)}
                </button>
              ))}
            </div>

            {/* Weekly day picker */}
            {recurrence === 'weekly' && (
              <>
                <label className="app-body mb-2 block font-medium text-slate-600">
                  {t('growth_todo_weekly_days')}
                </label>
                <div className="flex gap-1 mb-4">
                  {weekdayLabels.map((label, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        triggerLightHaptic();
                        toggleDay(i);
                      }}
                      className={cn(
                        'app-caption h-9 w-9 rounded-full border font-medium transition-all',
                        recurrenceDays.includes(i)
                          ? 'text-[#1D4ED8]'
                          : 'border-white/70 bg-white/80 text-[#2F3E33]'
                      )}
                      style={recurrenceDays.includes(i) ? selectedGlowStyle : undefined}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Priority */}
            <label className="app-body mb-2 block font-medium text-slate-600">
              {t('growth_todo_priority')}
            </label>
            <div className="flex gap-2 mb-4">
              {priorities.map((p) => (
                <button
                  key={p}
                  onClick={() => {
                    triggerLightHaptic();
                    setPriority(p);
                  }}
                  className={cn(
                    'app-body flex-1 rounded-xl border py-2 font-medium transition-all',
                    priority === p
                      ? GROWTH_PRIORITY_TEXT_CLASS[p]
                      : 'border-white/80 bg-white/70 text-[#2F3E33]'
                  )}
                  style={priority === p ? getGrowthPrioritySelectedStyle(p) : undefined}
                >
                  {t(`growth_todo_priority_${p}`)}
                </button>
              ))}
            </div>

            {/* Link bottle */}
            {bottles.length > 0 && (
              <>
                <label className="app-body mb-2 block font-medium text-slate-600">
                  {t('growth_todo_link_bottle')}
                </label>
                <AppSelectMenu
                  value={bottleId}
                  onChange={setBottleId}
                  ariaLabel={t('growth_todo_link_bottle')}
                  placement="top"
                  className="mb-2"
                  options={[
                    { value: '', label: t('growth_todo_none') },
                    ...bottles.map(bottle => ({ value: bottle.id, label: bottle.name })),
                  ]}
                />
              </>
            )}
          </div>

          <div className="border-t border-white/70 bg-white px-5 pb-[calc(env(safe-area-inset-bottom,0px)+10px)] pt-3">
            <button
              onClick={() => {
                if (!title.trim()) {
                  handleSubmit();
                  return;
                }
                triggerLightHaptic();
                handleSubmit();
              }}
              className="app-body w-full rounded-2xl py-2.5 font-medium transition-opacity"
              style={{
                ...APP_GREEN_GLASS_BUTTON_STYLE,
                color: APP_GREEN_GLASS_TEXT,
              }}
            >
              {t('confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
